import type { Mark } from '@atlaskit/editor-prosemirror/model';

import type { MarkSpan } from './types';
import { stableStringify } from './utils';

export type MarkDelta = {
	from: number;
	mark: Mark;
	to: number;
	type: 'insert' | 'delete';
};

const markKey = (m: Mark) => `${m.type.name}:${stableStringify(m.attrs ?? {})}`;
const sameMarks = (a: readonly Mark[], b: readonly Mark[]): boolean => {
	if (a.length !== b.length) return false;
	const bKeys = new Set(b.map(markKey));
	return a.every((m) => bKeys.has(markKey(m)));
};

/**
 * Returns marks present in `as` but not in `bs` (by type + attrs).
 */
function subtractMarks(as: readonly Mark[], bs: readonly Mark[]): Mark[] {
	const bKeys = new Set(bs.map(markKey));
	return as.filter((m) => !bKeys.has(markKey(m)));
}

/**
 * Sweep-line diff over mark spans. Given old and new mark coverage on the same
 * coordinate space, emit a minimal set of insert/delete deltas per sub-range.
 *
 * Mirrors `MarkUtils.findMarkDeltasFromMarkSpans` in the Kotlin reconciler.
 */
export function findMarkDeltas(
	oldSpans: readonly MarkSpan[],
	newSpans: readonly MarkSpan[],
): MarkDelta[] {
	type Ev = {
		fromOld: boolean;
		isStart: boolean;
		marks: readonly Mark[];
		pos: number;
	};
	const events: Ev[] = [];
	for (const s of oldSpans) {
		events.push({ pos: s.start, isStart: true, fromOld: true, marks: s.marks });
		events.push({ pos: s.end, isStart: false, fromOld: true, marks: s.marks });
	}
	for (const s of newSpans) {
		events.push({ pos: s.start, isStart: true, fromOld: false, marks: s.marks });
		events.push({ pos: s.end, isStart: false, fromOld: false, marks: s.marks });
	}
	// Sort by pos; for equal positions, OLD events first so the new state
	// takes effect for any sub-range starting at that boundary.
	events.sort((a, b) => a.pos - b.pos || (a.fromOld === b.fromOld ? 0 : a.fromOld ? -1 : 1));

	const deltas: MarkDelta[] = [];
	let curOld: readonly Mark[] = [];
	let curNew: readonly Mark[] = [];
	let last = 0;

	for (const ev of events) {
		if (!ev.isStart && last < ev.pos) {
			for (const m of subtractMarks(curNew, curOld)) {
				deltas.push({ type: 'insert', from: last, to: ev.pos, mark: m });
			}
			for (const m of subtractMarks(curOld, curNew)) {
				deltas.push({ type: 'delete', from: last, to: ev.pos, mark: m });
			}
		}
		if (ev.fromOld) curOld = ev.marks;
		else curNew = ev.marks;
		last = ev.pos;
	}
	return deltas;
}

/**
 * Returns the (sub-)spans of `spans` that overlap `[from, to)`, clipped to that range.
 * `spans` must be sorted by `start` and form a non-overlapping tiling.
 */
export function clipSpans(spans: readonly MarkSpan[], from: number, to: number): MarkSpan[] {
	if (from >= to) return [];
	const result: MarkSpan[] = [];
	for (const s of spans) {
		if (s.end <= from) continue;
		if (s.start >= to) break;
		result.push({
			start: Math.max(s.start, from),
			end: Math.min(s.end, to),
			marks: s.marks,
		});
	}
	return result;
}

export { sameMarks };
